import {
  BreadcrumbProps,
  Breadcrumbs,
  Classes,
  Colors,
} from "@blueprintjs/core";
import styled from "@emotion/styled";
import { Link, useParams } from "react-router";

import { Box } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { RoleplaySessionTabs } from "@/components/pages_logged_in/roleplays/details/RoleplaySessionTabs";
import {
  buildRoleplayUrl,
  buildScenarioUrl,
} from "@/components/pages_logged_in/roleplays/utils";
import { Container } from "@/components/pieces/bootstrap";
import { DateTimeDisplay } from "@/components/pieces/DateTimeDisplay";
import { RoleplayLimitBanner } from "@/components/pieces/roleplays/RoleplayLimitBanner";
import { RoleplaySessionMenu } from "@/components/pieces/roleplays/RoleplaySessionMenu";
import { Avatar } from "@/components/pieces/users/Avatar";
import { getUser } from "@/components/session/JwtTokenManager";
import { SUBTEXT_COLOR } from "@/css/constants";
import { VisibilityScope } from "@/graphql/index";
import { FullRoleplaySession, FullScenario } from "@/types/roleplays";

const ROLEPLAY_DASHBOARD_ROUTE = {
  href: "/roleplays",
  text: "Roleplay Dashboard",
};

interface RoleplaySessionHeaderProps {
  roleplay?: {
    id: string;
    name: string;
    slug: string;
    emojiUnicode: string;
  } | null;
  scenario?: FullScenario;
  roleplaySession?: FullRoleplaySession;
  loading?: boolean;
  px?: number;
  view?: string;
}

export function RoleplaySessionHeader({
  px = 0,
  view = "analysis",
  roleplay,
  scenario,
  roleplaySession,
  loading = false,
}: RoleplaySessionHeaderProps) {
  const { roleplaySlug } = useParams();

  const breadcrumbs: BreadcrumbProps[] | undefined = useBreadcrumbs({
    roleplay,
    scenario,
    roleplaySlug,
  });

  function breadcrumbRenderer(props: BreadcrumbProps) {
    if (loading) {
      return <Box className={Classes.SKELETON} height={20} width={83} />;
    }
    return (
      <StyledBreadcrumb title={String(props.text)}>
        {props.href ? <Link to={props.href}>{props.text}</Link> : props.text}
      </StyledBreadcrumb>
    );
  }

  return (
    <>
      <Box className="bbs" pt={[3, 4]} bg={Colors.WHITE} px={px}>
        <Container>
          <div className="mb-3 md:mb-4">
            <Breadcrumbs
              items={breadcrumbs}
              breadcrumbRenderer={breadcrumbRenderer}
              overflowListProps={{ alwaysRenderOverflow: true }}
            />
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
            <div className="flex-1 min-w-0">
              <Box
                fontWeight={600}
                fontSize={[22, 26]}
                lineHeight={1.2}
                className={loading ? Classes.SKELETON : ""}
                width={loading ? [280, 400] : "auto"}
                height={loading ? [28, 33] : "auto"}
              >
                <HeaderTitle roleplaySession={roleplaySession} />
              </Box>
              {roleplaySession && (
                <Subheader roleplaySession={roleplaySession} />
              )}
            </div>
            {roleplaySession && (
              <Media greaterThan="xs">
                <RoleplaySessionMenu
                  roleplaySession={roleplaySession}
                  showAccessButton={true}
                />
              </Media>
            )}
          </div>
          <Media lessThan="sm">
            {roleplaySession && (
              <div className="mt-3">
                <RoleplaySessionMenu
                  roleplaySession={roleplaySession}
                  showAccessButton={true}
                />
              </div>
            )}
          </Media>
          <div className="mt-3 md:mt-4">
            <RoleplaySessionTabs
              roleplaySlug={roleplay?.slug}
              scenario={scenario}
              roleplaySession={roleplaySession}
              view={view}
            />
          </div>
        </Container>
      </Box>
      {!loading && <RoleplayLimitBanner page="sessionDetail" />}
    </>
  );
}

function useBreadcrumbs({
  roleplay,
  scenario,
  roleplaySlug,
}: {
  roleplay?: RoleplaySessionHeaderProps["roleplay"];
  scenario?: FullScenario;
  roleplaySlug?: string;
}): BreadcrumbProps[] {
  const user = getUser();
  const isScenarioOwner = scenario?.owner?.id === user?.id;
  const couldAccessScenarios =
    isScenarioOwner ||
    (scenario?.visibilityScope === VisibilityScope.User && isScenarioOwner) ||
    (scenario?.visibilityScope === VisibilityScope.Workspace &&
      user?.workspaces) ||
    scenario?.visibilityScope === VisibilityScope.Global;
  const roleplayText = roleplay?.name || "Loading";
  const roleplayUrl = roleplay?.slug ? buildRoleplayUrl(roleplay.slug) : "#";

  const preRoutes = !roleplaySlug
    ? [ROLEPLAY_DASHBOARD_ROUTE]
    : [ROLEPLAY_DASHBOARD_ROUTE, { href: roleplayUrl, text: roleplayText }];

  return [
    ...preRoutes,
    {
      href: couldAccessScenarios
        ? buildScenarioUrl({
            roleplaySlug: roleplay?.slug,
            scenarioSlug: scenario?.slug ?? "",
          })
        : undefined,
      text: scenario?.name || "Loading",
    },
    { text: "Session Feedback" },
  ];
}

function HeaderTitle({
  roleplaySession,
}: {
  roleplaySession?: FullRoleplaySession;
}) {
  if (!roleplaySession || !roleplaySession.createdAt) {
    return (
      <Box
        mt={2}
        className={Classes.SKELETON}
        height={[20, 22]}
        width={[240, 300]}
      />
    );
  }
  return (
    <h1 className="m-0 text-[22px] md:text-[28px]">
      <span className="hidden sm:inline">Session on </span>
      <DateTimeDisplay
        time={roleplaySession.createdAt}
        formatString="MMM do, yyyy 'at' h:mm a"
      />
    </h1>
  );
}

function Subheader({
  roleplaySession,
}: {
  roleplaySession: FullRoleplaySession;
}) {
  if (!roleplaySession || !roleplaySession.createdAt) {
    return (
      <Box
        mt={2}
        className={Classes.SKELETON}
        height={[20, 22]}
        width={[240, 300]}
      />
    );
  }
  return (
    <div className="flex gap-2 mt-3 md:mt-4 text-sm md:text-base text-gray-700">
      <Avatar user={roleplaySession.user} size={20} />
      <span className="truncate">{roleplaySession.user.name}</span>
    </div>
  );
}

const StyledBreadcrumb = styled(Box)`
  max-width: 120px;
  color: ${SUBTEXT_COLOR};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  display: inline-block;
  font-size: 13px;

  @media (min-width: 640px) {
    max-width: 200px;
    font-size: 14px;
  }

  a {
    font-weight: 600;
    color: ${SUBTEXT_COLOR};
  }
`;
